import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { Grid, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import {
  useDeleteBillingMeterOnBuildingMutation,
  useGetBuildingByIdQuery,
  usePostBillingMeterOnBuildingMutation,
} from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetSubscriptionsByBuildingIdQuery } from '@/api/recalc/endpoints/jobSubscriptionApiSlice';
import { useGetUnitsByAccountQuery } from '@/api/recalc/endpoints/unitsApiSlice';
import {
  useGetActuatorPlanQuery,
  useUpdateMachineLearningModelMutation,
} from '@/api/subscription-manager/predictiveHeatControlSlice';

import {
  useGetAlgorithmInputFormSpecificationByJobTypeQuery,
  useGetAlgorithmInputSpecificationByJobTypeQuery,
  useGetOccupancyPredictionAnalysisQuery,
  useLazyGetOccupancyPredictionAnalysisQuery,
} from '../../../api/subscription-manager/algorithmInputSlice';

import { usePostSubscriptionOnBuildingMutation } from '@/api/recalc/endpoints/buildingApiSlice';
import { useGetGatewaysByAccountQuery } from '@/api/recalc/endpoints/gatewayApiSlice';
import { useGetJobSpecificationsQuery } from '@/api/recalc/endpoints/jobSpecificationApiSlice';
import {
  useDeleteJobSubscriptionMutation,
  usePostSubscriptionMutation,
  useUpdateSubscriptionMutation,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';

import SubscriptionDataGrid from '@/features/building-optimization/components/datagrid/SubscriptionDataGrid';
import AlgorithmInputAdvancedDialog from '@/features/building-optimization/components/dialog/algorithm-input/AlgorithmInputAdvancedDialog';
import AlgorithmInputSimpleDialog from '@/features/building-optimization/components/dialog/algorithm-input/AlgorithmInputSimpleDialog';
import OccupancyPredictionForm from '@/features/building-optimization/components/OccupancyPredictionForm';

import DeleteSubscriptionDialog from '@/features/building-optimization/components/dialog/DeleteSubscriptionDialog';
import JobSubscriptionCreate from '@/features/building-optimization/components/dialog/JobSubscriptionCreate';
import JobSubscriptionEdit from '@/features/building-optimization/components/dialog/JobSubscriptionEdit';
import ResetSubscriptionDialog from '@/features/building-optimization/components/dialog/ResetSubscriptionDialog';
import EditBuildingBillingMeterDialog from '@/features/building-optimization/components/dialog/EditBuildingBillingMeterDialog';

function extractNumberFromURL(url) {
  const matches = url?.match(/(\d+)$/);

  if (matches) {
    return parseInt(matches[1], 10);
  } else {
    return null;
  }
}

function formatAndEncodeTimestampForAPI(date) {
  // Convert the date to ISO format with the local timezone offset
  const isoString = DateTime.fromJSDate(date).toISO(); // Uses the local time zone by default
  return encodeURIComponent(isoString); // Encode for URL
}

const BuildingDetailPage = () => {
  const account = useSelector(getSelectedAccount);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selectedBuilding = location.state?.building;
  const accounts = location.state?.accounts;

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const jobType = selectedSubscription?.JobType;

  const { data: algorithmInputSpec } =
    useGetAlgorithmInputSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const { data: algorithmInputFormSpec } =
    useGetAlgorithmInputFormSpecificationByJobTypeQuery(
      { jobType },
      { skip: !selectedSubscription?.JobType },
    );

  const [openCreateSubscription, setOpenCreateSubscription] = useState(false);
  const [openEditSubscription, setOpenEditSubscription] = useState(false);
  const [openDeleteSubscription, setOpenDeleteSubscription] = useState(false);
  const [openResetModel, setOpenResetModel] = useState(false);
  const [algorithmInputOpen, setAlgorithmInputOpen] = useState(false);
  const [algorithmInputFormOpen, setAlgorithmInputFormOpen] = useState(false);

  const [futureTimeRange, setFutureTimeRange] = useState({
    start: DateTime.now().toUTC().toMillis(), // Store UTC
    end: DateTime.now().plus({ days: 7 }).toUTC().toMillis(), // Store UTC
  });

  const [modelAnalysisDialogOpen, setModelAnalysisDialogOpen] = useState(false);
  const [generatedOccupancyPlan, setGeneratedOccupancyPlan] = useState(null);
  const [isGeneratingOccupancyAnalysis, setIsGeneratingOccupancyAnalysis] =
    useState(false);

  const [
    openEditBuildingBillingMetersDialog,
    setOpenEditBuildingBillingMetersDialog,
  ] = useState(false);

  const [updateMachineLearningModel] = useUpdateMachineLearningModelMutation();

  const { data: subscriptions, refetch: refetchSubscriptions } =
    useGetSubscriptionsByBuildingIdQuery(selectedBuilding.BuildingId, {
      skip: !selectedBuilding?.BuildingId,
      // pollingInterval: 10000,
    });

  const { data: building } = useGetBuildingByIdQuery(
    selectedBuilding?.BuildingId,
    {
      skip: !selectedBuilding?.BuildingId,
    },
  );

  const { data: units } = useGetUnitsByAccountQuery(
    selectedBuilding.AccountId,
    {
      skip: !selectedBuilding.AccountId,
    },
  );

  const { data: gateways } = useGetGatewaysByAccountQuery(
    selectedBuilding.AccountId,
    {
      skip: !selectedBuilding?.AccountId,
    },
  );

  const { data: jobSpecifications } = useGetJobSpecificationsQuery();

  const { data: publishedActuatorPlan } = useGetActuatorPlanQuery(
    { id: selectedSubscription?.JobSubscriptionId },
    {
      skip:
        !selectedSubscription?.JobSubscriptionId ||
        !selectedSubscription.JobType ||
        selectedSubscription.JobType !== 'PredictiveHeatControl',
    },
  );

  const [fetchOccupancyPredictionAnalysis] =
    useLazyGetOccupancyPredictionAnalysisQuery();

  const occupancySourceExclusions =
    selectedSubscription?.AlgorithmInput?.predictive_occupancy_detection
      ?.general?.occupancy_source_exclusion || [];

  const { data: occupancyPrediction } = useGetOccupancyPredictionAnalysisQuery(
    {
      jobSubscriptionId: selectedSubscription?.JobSubscriptionId,
      start: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.start)),
      end: formatAndEncodeTimestampForAPI(new Date(futureTimeRange.end)),
      exclusions: occupancySourceExclusions,
    },
    { skip: !selectedSubscription?.JobSubscriptionId },
  );

  const [postSubscription] = usePostSubscriptionMutation();
  const [deleteSubscription] = useDeleteJobSubscriptionMutation();
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [postSubscriptionOnBuilding] = usePostSubscriptionOnBuildingMutation();
  const [postBillingMeter] = usePostBillingMeterOnBuildingMutation();
  const [deleteBillingMeter] = useDeleteBillingMeterOnBuildingMutation();

  const handleViewSubscription = (subscription) => {
    navigate(
      `/PVO/buildings/${encodeURIComponent(selectedBuilding.Name)}/${encodeURIComponent(subscription.DisplayName)}`,
      {
        state: {
          subscription: subscription,
          building: building,
          from: location, // store current location
        },
      },
    );
  };

  const handleAddBillingMeter = async (billingMeter) => {
    try {
      const response = await postBillingMeter({
        buildingId: building.BuildingId,
        billingMeter,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Billing meter was added',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error adding billing meter',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleDeleteBillingMeter = async (billingMeterId) => {
    try {
      const response = await deleteBillingMeter({
        buildingId: building.BuildingId,
        billingMeterId,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Billing meter was removed',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error removing billing meter',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleCreateSubscription = async (subscription) => {
    try {
      const response = await postSubscription(subscription).unwrap();
      if (response) {
        dispatch(
          showSnackbar({
            message: 'Subscription was created',
            severity: 'success',
            response: response,
          }),
        );
        const subscriptionId = extractNumberFromURL(response.location);

        if (subscriptionId && building) {
          try {
            const secondResponse = await postSubscriptionOnBuilding({
              buildingId: building.BuildingId,
              subscriptionId,
            });

            dispatch(
              showSnackbar({
                message: `Subscription was added to ${building.Name}`,
                severity: 'success',
                response: secondResponse,
              }),
            );
          } catch (error) {
            dispatch(
              showSnackbar({
                message: 'Error adding subscription to building',
                severity: 'error',
                response: error,
              }),
            );
          }
        }
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error creating subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  const handleUpdateSubscription = async (subscription) => {
    try {
      const response = await updateSubscription({
        id: selectedSubscription.JobSubscriptionId,
        patch: subscription,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Subscription was updated',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error updating subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
  };

  // * Move to DeleteSubscriptionDialog
  const handleDeleteSubscriptionConfirmation = async () => {
    try {
      const response = await deleteSubscription(
        selectedSubscription.JobSubscriptionId,
      ).unwrap();
      dispatch(
        showSnackbar({
          message: 'Subscription was deleted',
          severity: 'success',
          response: response,
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error deleting subscription',
          severity: 'error',
          response: error,
        }),
      );
    }
    setOpenDeleteSubscription(false);
  };

  const handleResetMachineLearningModel = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenResetModel(true);
  };

  const handleResetMachineLearningModelConfirmation = async (body) => {
    try {
      await updateMachineLearningModel({
        id: selectedSubscription.JobSubscriptionId,
        data: body,
      }).unwrap();
      dispatch(
        showSnackbar({
          message: 'Machine learning model reset successfully',
          severity: 'success',
        }),
      );
    } catch (error) {
      dispatch(
        showSnackbar({
          message: 'Error resetting machine learning model',
          severity: 'error',
          response: error,
        }),
      );
    }
    setOpenResetModel(false);
  };

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenEditSubscription(true);
  };

  const handleDeleteSubscription = (subscription) => {
    setSelectedSubscription(subscription);
    setOpenDeleteSubscription(true);
  };

  const handleAlgorithmInputOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputOpen(true);
  };

  const handleAlgorithmInputFormOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setAlgorithmInputFormOpen(true);
  };

  const handleOccupancyPredictionAnalysisOpen = (subscription) => {
    setSelectedSubscription(subscription);
    setModelAnalysisDialogOpen(true);
  };

  const generateOccupancyPredictionAnalysis = async (formData) => {
    setIsGeneratingOccupancyAnalysis(true); // Start loading

    try {
      const result = await fetchOccupancyPredictionAnalysis({
        jobSubscriptionId: selectedSubscription.JobSubscriptionId,
        duration: formData.duration,
        filterErraticPredictions: formData.filterErraticPredictions,
        exclusions: formData.exclusions,
      });

      // Log the raw result before unwrapping or processing it.

      if (result.data) {
        setGeneratedOccupancyPlan(result.data.occupancy_plan);
      } else {
        dispatch(
          showSnackbar({
            message: result.error.data.detail.ErrorMessage,
            severity: 'error',
          }),
        );
        console.warn('No data in response');
      }
    } catch (error) {
      console.error('Error fetching occupancy prediction analysis:', error);
      setGeneratedOccupancyPlan(null);
    } finally {
      setIsGeneratingOccupancyAnalysis(false); // Stop loading
    }
  };

  if (!building) return null;

  return (
    <>
      <Stack gap={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Suspense fallback={<div>Loading Building Info...</div>}>
              <SubscriptionDataGrid
                subscriptions={
                  subscriptions?.filter(
                    (s) => s.JobType === 'PredictiveHeatControl',
                  ) || []
                }
                accounts={accounts}
                onSubscriptionCreate={() => setOpenCreateSubscription(true)}
                onSubscriptionDelete={handleDeleteSubscriptionConfirmation}
                handleAlgorithmInputFormOpen={handleAlgorithmInputFormOpen}
                handleAlgorithmInputOpen={handleAlgorithmInputOpen}
                handleOccupancyPredictionAnalysisOpen={
                  handleOccupancyPredictionAnalysisOpen
                }
                handleViewSubscription={handleViewSubscription}
                handleEditSubscription={handleEditSubscription}
                handleDeleteSubscription={handleDeleteSubscription}
                handleResetMachineLearningModel={
                  handleResetMachineLearningModel
                }
                refetchSubscriptions={refetchSubscriptions}
              />
            </Suspense>
          </Grid>
        </Grid>
      </Stack>

      <Suspense fallback={<div>Loading Create Subscription Dialog...</div>}>
        <JobSubscriptionCreate
          open={openCreateSubscription}
          onClose={() => setOpenCreateSubscription(false)}
          selectedAccount={account}
          location={selectedBuilding?.location}
          selectedBuilding={building}
          units={units}
          jobSpecifications={jobSpecifications}
          submit={handleCreateSubscription}
        />
      </Suspense>

      <Suspense fallback={<div>Loading Create Subscription Dialog...</div>}>
        <JobSubscriptionEdit
          open={openEditSubscription}
          onClose={() => setOpenEditSubscription(false)}
          submit={handleUpdateSubscription}
          subscription={selectedSubscription}
          location={selectedBuilding?.location}
          jobSpecifications={jobSpecifications}
          units={units}
          gateways={gateways}
        />
      </Suspense>

      <Suspense fallback={<div>Loading Edit Billing Meters Dialog...</div>}>
        <EditBuildingBillingMeterDialog
          open={openEditBuildingBillingMetersDialog}
          onClose={() => setOpenEditBuildingBillingMetersDialog(false)}
          options={units}
          billingMeters={building?.BillingMeters || []}
          handleAddBillingMeter={handleAddBillingMeter}
          handleDeleteBillingMeter={handleDeleteBillingMeter}
        />
      </Suspense>

      <Suspense fallback={<div>Loading Delete Subscription Dialog...</div>}>
        <DeleteSubscriptionDialog
          open={openDeleteSubscription}
          onClose={() => setOpenDeleteSubscription(false)}
          subscription={selectedSubscription}
          handleDeleteSubscription={handleDeleteSubscriptionConfirmation}
        />
      </Suspense>

      <ResetSubscriptionDialog
        open={openResetModel}
        onClose={() => setOpenResetModel(false)}
        subscription={selectedSubscription}
        handleResetSubscription={handleResetMachineLearningModelConfirmation}
      />

      <Suspense fallback={<div>Loading algorithm input dialog...</div>}>
        <AlgorithmInputAdvancedDialog
          open={algorithmInputOpen}
          onClose={() => setAlgorithmInputOpen(false)}
          subscription={selectedSubscription}
          update={handleUpdateSubscription}
          specificationData={algorithmInputSpec}
        />
      </Suspense>

      {/* <Suspense fallback={<div>Loading algorithm input dialog...</div>}> */}
      <AlgorithmInputSimpleDialog
        open={algorithmInputFormOpen}
        onClose={() => setAlgorithmInputFormOpen(false)}
        subscription={selectedSubscription}
        algorithmInput={selectedSubscription?.AlgorithmInput || {}}
        update={handleUpdateSubscription}
        specificationData={algorithmInputFormSpec}
        updateMachineLearningModel={updateMachineLearningModel}
      />
      {/* </Suspense> */}

      <Suspense fallback={<div>Loading occupancy prediction form...</div>}>
        <OccupancyPredictionForm
          open={modelAnalysisDialogOpen}
          onClose={() => {
            setModelAnalysisDialogOpen(false);
            setGeneratedOccupancyPlan(null);
          }}
          subscription={selectedSubscription}
          actuatorPlan={publishedActuatorPlan}
          initialPrediction={occupancyPrediction}
          occupancySourceExclusions={occupancySourceExclusions}
          generateOccupancyPredictionAnalysis={
            generateOccupancyPredictionAnalysis
          }
          setGeneratedOccupancyPlan={() => setGeneratedOccupancyPlan(null)}
          generatedOccupancyPlan={generatedOccupancyPlan}
          isGenerating={isGeneratingOccupancyAnalysis}
        />
      </Suspense>

      <Outlet />
    </>
  );
};

export default BuildingDetailPage;
